/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

import PropTypes from "prop-types";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import ReferencePicker from "./ReferencePicker";
import { formatEnumLabel } from "./formatters";

/**
 * Form inputs from a field spec, shared by the add dialog and a detail page's
 * edit mode.
 *
 * Field spec: { name, label, type, options, required, fullWidth, reference,
 * helperText, adornment, labelField, visibleWhen(values) }.
 * type: text | textarea | number | money | pct | date | datetime | select |
 *       boolean | email | reference | list (comma separated -> array)
 * reference: { apiPath, params | params(values), getLabel } and the record's
 * display name read from values[field.labelField].
 */

const normalizeOptions = (options) =>
  (options || []).map((o) =>
    typeof o === "object" && o !== null ? o : { value: o, label: formatEnumLabel(o) }
  );

/** Convert a form value to what the API expects for that field type. */
export function toPayloadValue(field, raw) {
  if (raw === "" || raw === undefined) return null;
  switch (field.type) {
    case "number":
    case "money":
    case "pct": {
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
    case "boolean":
      return Boolean(raw);
    case "list":
      if (Array.isArray(raw)) return raw;
      return String(raw)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    default:
      return raw;
  }
}

/** Convert an API value to a form value. */
export function toFormValue(field, value) {
  if (value === null || value === undefined) {
    return field.type === "boolean" ? false : "";
  }
  if (field.type === "list" && Array.isArray(value)) return value.join(", ");
  if (field.type === "date" && typeof value === "string") return value.slice(0, 10);
  if (field.type === "datetime" && typeof value === "string") return value.slice(0, 16);
  return value;
}

export function initialFormValues(fields, record = null, defaults = {}) {
  const values = {};
  (fields || []).forEach((field) => {
    const source =
      record && record[field.name] !== undefined
        ? record[field.name]
        : defaults[field.name] !== undefined
          ? defaults[field.name]
          : field.defaultValue;
    values[field.name] = toFormValue(field, source);
    if (field.labelField && record) values[field.labelField] = record[field.labelField];
  });
  return values;
}

export function buildPayload(fields, values, { onlyChangedFrom = null } = {}) {
  const payload = {};
  (fields || []).forEach((field) => {
    if (field.readOnly) return;
    if (field.visibleWhen && !field.visibleWhen(values)) return;
    const next = toPayloadValue(field, values[field.name]);
    if (onlyChangedFrom) {
      const prev = toPayloadValue(field, toFormValue(field, onlyChangedFrom[field.name]));
      if (JSON.stringify(prev) === JSON.stringify(next)) return;
    }
    payload[field.name] = next;
  });
  return payload;
}

export function missingRequired(fields, values) {
  return (fields || [])
    .filter((f) => f.required && !(f.visibleWhen && !f.visibleWhen(values)))
    .filter((f) => {
      const v = values[f.name];
      return v === "" || v === null || v === undefined;
    })
    .map((f) => f.label);
}

const EntityFormFields = ({ fields, values, onChange, disabled, size }) => (
  <Grid container spacing={2}>
    {(fields || [])
      .filter((field) => !field.visibleWhen || field.visibleWhen(values))
      .map((field) => {
        const value = values[field.name];
        const common = {
          fullWidth: true,
          size,
          label: field.label,
          required: field.required,
          disabled: disabled || field.readOnly,
          helperText: field.helperText,
        };
        let input;
        switch (field.type) {
          case "select":
            input = (
              <TextField
                {...common}
                select
                value={value ?? ""}
                onChange={(e) => onChange(field.name, e.target.value)}
              >
                {!field.required ? (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                ) : null}
                {normalizeOptions(field.options).map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            );
            break;
          case "boolean":
            input = (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(value)}
                    disabled={common.disabled}
                    onChange={(e) => onChange(field.name, e.target.checked)}
                  />
                }
                label={field.label}
              />
            );
            break;
          case "reference": {
            const ref = field.reference || {};
            const params = typeof ref.params === "function" ? ref.params(values) : ref.params;
            input = (
              <ReferencePicker
                apiPath={ref.apiPath}
                params={params}
                getLabel={ref.getLabel}
                value={value === "" ? null : value}
                initialLabel={field.labelField ? values[field.labelField] : ""}
                label={field.label}
                required={field.required}
                disabled={common.disabled}
                size={size}
                helperText={field.helperText}
                onChange={(id) => onChange(field.name, id ?? "")}
              />
            );
            break;
          }
          case "textarea":
            input = (
              <TextField
                {...common}
                multiline
                minRows={3}
                value={value ?? ""}
                onChange={(e) => onChange(field.name, e.target.value)}
              />
            );
            break;
          case "date":
          case "datetime":
            input = (
              <TextField
                {...common}
                type={field.type === "date" ? "date" : "datetime-local"}
                InputLabelProps={{ shrink: true }}
                value={value ?? ""}
                onChange={(e) => onChange(field.name, e.target.value)}
              />
            );
            break;
          case "number":
          case "money":
          case "pct":
            input = (
              <TextField
                {...common}
                type="number"
                value={value ?? ""}
                onChange={(e) => onChange(field.name, e.target.value)}
                InputProps={
                  field.type === "pct" || field.adornment
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            {field.adornment || "%"}
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            );
            break;
          default:
            input = (
              <TextField
                {...common}
                type={field.type === "email" ? "email" : "text"}
                value={value ?? ""}
                onChange={(e) => onChange(field.name, e.target.value)}
              />
            );
        }
        return (
          <Grid item xs={12} md={field.fullWidth || field.type === "textarea" ? 12 : 6} key={field.name}>
            {input}
          </Grid>
        );
      })}
  </Grid>
);

EntityFormFields.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  size: PropTypes.string,
};

EntityFormFields.defaultProps = {
  disabled: false,
  size: "small",
};

export default EntityFormFields;
